import React from "react";
import { FormikProvider, useFormik, Form } from "formik";
import { z } from "zod";
import { Entity } from "@common/types/entity";
import { UserWorkspaceRole } from "@common/userWorkspaceRoles";
import { HOTKEYS_SCOPES } from "@hooks/keyboard/useKeyboardCommand";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { StandardModalHeader } from "@design-system/Modal/Modal";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Button } from "@design-system/Button/Button";
import { AddMemberFormSchema } from "./types";
import styles from "./MemberManagement.module.scss";
import { AddTeamFields } from "./AddTeamFields";

const addMemberFormSchema = z.object({
    emails: z
        .array(z.string().email("All emails must be valid"))
        .min(1, "At least one email is required"),
    entityAssignments: z
        .record(z.string(), z.boolean())
        .refine(
            (assignments) => Object.values(assignments).some(Boolean),
            "At least one entity must be selected",
        ),
    workspaceRole: z.nativeEnum(UserWorkspaceRole),
    assignToNewEntities: z.boolean(),
});

interface Props {
    actingUserRole: UserWorkspaceRole;
    entities: Array<
        Pick<
            Entity,
            | "id"
            | "name"
            | "isBusiness"
            | "workspaceId"
            | "user"
            | "iconColor"
            | "logo"
        >
    >;
    header?: React.ReactNode;
    providedEmail?: string;
    providedRole?: UserWorkspaceRole;
    closeModal: () => void;
    submitHandler: (value: AddMemberFormSchema) => Promise<void>;
    initialEntityIds?: number[];
}

export const AddMemberModal: React.FC<Props> = ({
    actingUserRole,
    entities,
    header = "Add member to workspace",
    providedEmail,
    providedRole,
    closeModal,
    submitHandler,
    initialEntityIds,
}) => {
    const formik = useFormik<AddMemberFormSchema>({
        initialValues: {
            emails: providedEmail ? [providedEmail] : [],
            workspaceRole: providedRole ?? UserWorkspaceRole.MEMBER,
            entityAssignments: entities.reduce(
                (acc, entity) => {
                    acc[entity.id] =
                        initialEntityIds?.includes(entity.id) ?? false;
                    return acc;
                },
                {} as Record<string, boolean>,
            ),
            assignToNewEntities: false,
        },
        validate: validateFormikWithZodSchema(addMemberFormSchema),
        onSubmit: submitHelper({
            handler: submitHandler,
        }),
    });

    return (
        <>
            <StandardModalHeader>{header}</StandardModalHeader>

            <section className={styles.layout}>
                <FormikProvider value={formik}>
                    <Form>
                        <main className={styles.teamForm}>
                            <AddTeamFields
                                actingUserRole={actingUserRole}
                                entities={entities}
                            />

                            <footer className={styles.footer}>
                                <Button
                                    type="button"
                                    variant="secondary"
                                    disabled={formik.isSubmitting}
                                    size="xl"
                                    onClick={closeModal}
                                >
                                    Cancel
                                </Button>

                                <ButtonWithLoader
                                    loading={formik.isSubmitting}
                                    type="submit"
                                    variant="primary"
                                    size="xl"
                                    disabled={
                                        !formik.isValid || formik.isSubmitting
                                    }
                                    hotKey="mod+enter"
                                    hotKeyOptions={{
                                        description: "Send invites",
                                        enableOnFormTags: true,
                                        enableOnContentEditable: true,
                                        scopes: [HOTKEYS_SCOPES.GLOBAL],
                                    }}
                                >
                                    Send invites
                                </ButtonWithLoader>
                            </footer>
                        </main>
                    </Form>
                </FormikProvider>
            </section>
        </>
    );
};
